import React, { useEffect, useState } from "react";
import { Shell } from "../components/Shell";
import { Tabs } from "../components/Tabs";
import { api, ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";

type Proveedor = {
  id: number; nombre: string; nit: string | null; telefono: string | null; email: string | null; activo: boolean;
  tipoPersona: string; regimenTributario: string; regimenIva: string; tarifaIvaPct: number;
  autorretenedor: boolean; granContribuyente: boolean; actividadEconomicaCiiu: string | null; municipioIca: string | null;
};
type Orden = { id: number; proveedorId: number; proveedorNombre: string; numero: string; fecha: string; estado: string; observaciones: string | null };
type Recepcion = {
  id: number; ordenCompraId: number | null; especieId: number; especieNombre: string; bodegaId: number; bodegaNombre: string;
  proveedorId: number | null; proveedorNombre: string | null;
  loteCodigo: string; fecha: string; pesoBrutoKg: number; pesoNetoKg: number; precioCompraKg: number; consumidaEnLote: boolean;
};
type Opcion = { id: number; nombre: string };
type ItemFacturaCompra = { producto: string; cantidad: number; valorUnitario: number; valorVenta: number | null };
type FacturaCompra = {
  id: number; proveedorId: number; proveedorNombre: string; numeroFactura: string; fecha: string;
  bodegaId: number; bodegaNombre: string; base: number; observaciones: string | null;
  comprobanteId: number | null; items: ItemFacturaCompra[];
};
type ProductoCatalogo = { id: number; nombre: string; unidadVenta: string; codigoBarras: string | null; activo: boolean };
type DocumentoSoporte = {
  id: number; proveedorId: number; proveedorNombre: string; numero: string; fecha: string; concepto: string;
  tipoDestino: string; base: number; observaciones: string | null; estado: string;
  bodegaId: number | null; especieId: number | null; cantidadKg: number | null;
};

const ESTADO_ORDEN_BADGE: Record<string, string> = {
  borrador: "bg-slate-100 text-slate-500", aprobada: "bg-amber-50 text-amber-700",
  recibida: "bg-accent-light text-accent-dark", cancelada: "bg-red-50 text-red-700",
};

export function Compras() {
  const { tienePermiso } = useAuth();
  const puedeCrear = tienePermiso("compras:crear");
  const puedeEditar = tienePermiso("compras:editar");
  const puedeEliminar = tienePermiso("compras:eliminar");

  const [tab, setTab] = useState("proveedores");
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);
  const [ordenes, setOrdenes] = useState<Orden[]>([]);
  const [recepciones, setRecepciones] = useState<Recepcion[]>([]);
  const [especies, setEspecies] = useState<Opcion[]>([]);
  const [bodegas, setBodegas] = useState<Opcion[]>([]);
  const [documentosSoporte, setDocumentosSoporte] = useState<DocumentoSoporte[]>([]);
  const [facturasCompra, setFacturasCompra] = useState<FacturaCompra[]>([]);
  const [catalogoProductos, setCatalogoProductos] = useState<ProductoCatalogo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargarTodo = async () => {
    setCargando(true);
    setError(null);
    try {
      const [prov, ord, rec, cat, bod, ds, fc, prod] = await Promise.all([
        api.get("/compras/proveedores"),
        api.get("/compras/ordenes"),
        api.get("/compras/recepciones"),
        api.get("/catalogos?tipo=especie"),
        api.get("/inventarios/bodegas"),
        api.get("/compras/documentos-soporte"),
        api.get("/compras/facturas-compra"),
        api.get("/productos"),
      ]);
      setProveedores(prov);
      setOrdenes(ord);
      setRecepciones(rec);
      setEspecies(cat.map((c: any) => ({ id: c.id, nombre: c.nombre })));
      setBodegas(bod.map((b: any) => ({ id: b.id, nombre: b.nombre })));
      setDocumentosSoporte(ds);
      setFacturasCompra(fc);
      setCatalogoProductos(prod);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la información de compras.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarTodo(); }, []);

  return (
    <Shell titulo="Compras y Recepción" breadcrumb="Compras y Recepción">
      <p className="text-sm text-textmuted mb-4">
        Proveedores, órdenes de compra y recepción de materia prima. Cada recepción actualiza automáticamente el inventario.
        Las facturas de compra son la vía de entrada de mercancía lista para vender (negocios que revenden sin fabricar).
        El documento soporte electrónico cubre las compras a proveedores que no están obligados a facturar.
      </p>
      <Tabs
        tabs={[
          { key: "proveedores", label: "Proveedores" },
          { key: "ordenes", label: "Órdenes de compra" },
          { key: "recepciones", label: "Recepciones" },
          { key: "facturas-compra", label: "Facturas de compra" },
          { key: "documentos-soporte", label: "Documento soporte" },
        ]}
        activo={tab}
        onChange={setTab}
      />
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}
      {cargando ? (
        <p className="text-sm text-textmuted">Cargando...</p>
      ) : (
        <>
          {tab === "proveedores" && (
            <TabProveedores
              proveedores={proveedores} recargar={cargarTodo}
              puedeCrear={puedeCrear} puedeEditar={puedeEditar} puedeEliminar={puedeEliminar}
            />
          )}
          {tab === "ordenes" && (
            <TabOrdenes
              ordenes={ordenes} proveedores={proveedores} recargar={cargarTodo}
              puedeCrear={puedeCrear} puedeEditar={puedeEditar}
            />
          )}
          {tab === "recepciones" && (
            <TabRecepciones
              recepciones={recepciones} ordenes={ordenes} especies={especies} bodegas={bodegas} proveedores={proveedores}
              recargar={cargarTodo} puedeCrear={puedeCrear}
            />
          )}
          {tab === "facturas-compra" && (
            <TabFacturasCompra
              facturas={facturasCompra} proveedores={proveedores} bodegas={bodegas} catalogoProductos={catalogoProductos}
              recargar={cargarTodo} puedeCrear={puedeCrear}
            />
          )}
          {tab === "documentos-soporte" && (
            <TabDocumentosSoporte
              documentos={documentosSoporte} proveedores={proveedores} especies={especies} bodegas={bodegas}
              recargar={cargarTodo} puedeCrear={puedeCrear} puedeEditar={puedeEditar}
            />
          )}
        </>
      )}
    </Shell>
  );
}

// ------------------------------------------------------------- Proveedores
function TabProveedores({ proveedores, recargar, puedeCrear, puedeEditar, puedeEliminar }: {
  proveedores: Proveedor[]; recargar: () => void; puedeCrear: boolean; puedeEditar: boolean; puedeEliminar: boolean;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [nombre, setNombre] = useState("");
  const [nit, setNit] = useState("");
  const [telefono, setTelefono] = useState("");
  const [email, setEmail] = useState("");
  const [regimenIva, setRegimenIva] = useState("no_responsable");
  const [tarifaIvaPct, setTarifaIvaPct] = useState("0");
  const [regimenTributario, setRegimenTributario] = useState("ordinario");
  const [autorretenedor, setAutorretenedor] = useState(false);
  const [granContribuyente, setGranContribuyente] = useState(false);
  const [municipioIca, setMunicipioIca] = useState("");
  const [actividadCiiu, setActividadCiiu] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      await api.post("/compras/proveedores", {
        nombre, nit, telefono, email, regimenIva, tarifaIvaPct: Number(tarifaIvaPct) || 0,
        regimenTributario, autorretenedor, granContribuyente,
        municipioIca: municipioIca || null, actividadEconomicaCiiu: actividadCiiu || null,
      });
      setModalAbierto(false);
      setNombre(""); setNit(""); setTelefono(""); setEmail("");
      setRegimenIva("no_responsable"); setTarifaIvaPct("0"); setRegimenTributario("ordinario");
      setAutorretenedor(false); setGranContribuyente(false); setMunicipioIca(""); setActividadCiiu("");
      recargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo crear el proveedor.");
    } finally {
      setGuardando(false);
    }
  };

  const desactivar = async (p: Proveedor) => {
    try {
      await api.put(`/compras/proveedores/${p.id}`, { activo: !p.activo });
      recargar();
    } catch { /* noop */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo proveedor</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-borderc">
              <th className="table-th">Nombre</th><th className="table-th">NIT</th>
              <th className="table-th">Teléfono</th><th className="table-th">Email</th>
              <th className="table-th">Impuestos</th>
              <th className="table-th">Estado</th><th className="table-th"></th>
            </tr>
          </thead>
          <tbody>
            {proveedores.map((p) => (
              <tr key={p.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-medium text-slate-800">{p.nombre}</td>
                <td className="table-td">{p.nit}</td>
                <td className="table-td">{p.telefono}</td>
                <td className="table-td">{p.email}</td>
                <td className="table-td">
                  <div className="flex flex-wrap gap-1">
                    <span className="badge bg-navy/10 text-navy text-[10px]">
                      {p.regimenIva === "responsable" ? `IVA ${p.tarifaIvaPct}%` : "No resp. IVA"}
                    </span>
                    {p.autorretenedor && <span className="badge bg-amber-50 text-amber-700 text-[10px]">Autorretenedor</span>}
                    {p.granContribuyente && <span className="badge bg-amber-50 text-amber-700 text-[10px]">Gran contrib.</span>}
                    {p.regimenTributario === "simple" && <span className="badge bg-slate-100 text-slate-500 text-[10px]">Régimen simple</span>}
                  </div>
                </td>
                <td className="table-td"><span className={`badge ${p.activo ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>{p.activo ? "activo" : "inactivo"}</span></td>
                <td className="table-td text-right">
                  {puedeEditar && <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => desactivar(p)}>{p.activo ? "Desactivar" : "Activar"}</button>}
                </td>
              </tr>
            ))}
            {proveedores.length === 0 && <tr><td colSpan={7} className="table-td text-center text-textmuted py-8">Sin proveedores registrados.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nuevo proveedor</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Nombre</label>
              <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">NIT</label>
              <input className="input-field" value={nit} onChange={(e) => setNit(e.target.value)} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Teléfono</label>
              <input className="input-field" value={telefono} onChange={(e) => setTelefono(e.target.value)} /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
              <input className="input-field" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>

            <div className="border-t border-borderc pt-3">
              <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Atributos tributarios</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Régimen de IVA</label>
                  <select className="input-field" value={regimenIva} onChange={(e) => setRegimenIva(e.target.value)}>
                    <option value="no_responsable">No responsable de IVA</option>
                    <option value="responsable">Responsable de IVA</option>
                  </select></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Tarifa IVA (%)</label>
                  <input className="input-field" type="number" step="0.01" value={tarifaIvaPct}
                    onChange={(e) => setTarifaIvaPct(e.target.value)} disabled={regimenIva !== "responsable"} /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Régimen tributario</label>
                  <select className="input-field" value={regimenTributario} onChange={(e) => setRegimenTributario(e.target.value)}>
                    <option value="ordinario">Ordinario</option>
                    <option value="simple">Régimen simple</option>
                  </select></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Municipio ICA</label>
                  <input className="input-field" value={municipioIca} onChange={(e) => setMunicipioIca(e.target.value)} placeholder="Ej. Cali" /></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Actividad CIIU</label>
                  <input className="input-field" value={actividadCiiu} onChange={(e) => setActividadCiiu(e.target.value)} placeholder="Ej. 0322" /></div>
              </div>
              <div className="flex gap-4 mt-3">
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={autorretenedor} onChange={(e) => setAutorretenedor(e.target.checked)} /> Autorretenedor
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={granContribuyente} onChange={(e) => setGranContribuyente(e.target.checked)} /> Gran contribuyente
                </label>
              </div>
              <p className="text-[11px] text-textmuted mt-2">
                Estos atributos determinan qué IVA y retenciones (retefuente, ReteICA) calcula automáticamente el sistema
                al recibir mercancía de este proveedor — ver módulo de Contabilidad.
              </p>
            </div>

            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------- Ordenes
function TabOrdenes({ ordenes, proveedores, recargar, puedeCrear, puedeEditar }: {
  ordenes: Orden[]; proveedores: Proveedor[]; recargar: () => void; puedeCrear: boolean; puedeEditar: boolean;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [proveedorId, setProveedorId] = useState("");
  const [numero, setNumero] = useState("");
  const [fecha, setFecha] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      await api.post("/compras/ordenes", { proveedorId: Number(proveedorId), numero, fecha, observaciones });
      setModalAbierto(false);
      setProveedorId(""); setNumero(""); setFecha(""); setObservaciones("");
      recargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo crear la orden de compra.");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (o: Orden, estado: string) => {
    try {
      await api.put(`/compras/ordenes/${o.id}`, { estado });
      recargar();
    } catch { /* noop */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva orden</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-borderc">
              <th className="table-th">Número</th><th className="table-th">Proveedor</th>
              <th className="table-th">Fecha</th><th className="table-th">Estado</th><th className="table-th"></th>
            </tr>
          </thead>
          <tbody>
            {ordenes.map((o) => (
              <tr key={o.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs font-semibold">{o.numero}</td>
                <td className="table-td">{o.proveedorNombre}</td>
                <td className="table-td">{o.fecha}</td>
                <td className="table-td"><span className={`badge ${ESTADO_ORDEN_BADGE[o.estado]}`}>{o.estado}</span></td>
                <td className="table-td text-right">
                  {puedeEditar && o.estado === "borrador" && (
                    <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => cambiarEstado(o, "aprobada")}>Aprobar</button>
                  )}
                </td>
              </tr>
            ))}
            {ordenes.length === 0 && <tr><td colSpan={5} className="table-td text-center text-textmuted py-8">Sin órdenes de compra.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <form onSubmit={crear} className="card w-full max-w-md p-6 space-y-4">
            <h3 className="text-base font-semibold text-slate-800">Nueva orden de compra</h3>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Proveedor</label>
              <select className="input-field" value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} required>
                <option value="">Seleccione...</option>
                {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número</label>
              <input className="input-field font-mono" value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="OC-0002" required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
              <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones</label>
              <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></div>
            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Crear"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------- Recepciones
function TabRecepciones({ recepciones, ordenes, especies, bodegas, proveedores, recargar, puedeCrear }: {
  recepciones: Recepcion[]; ordenes: Orden[]; especies: Opcion[]; bodegas: Opcion[]; proveedores: Proveedor[]; recargar: () => void; puedeCrear: boolean;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [especieId, setEspecieId] = useState("");
  const [bodegaId, setBodegaId] = useState("");
  const [ordenCompraId, setOrdenCompraId] = useState("");
  const [proveedorId, setProveedorId] = useState("");
  const [loteCodigo, setLoteCodigo] = useState("");
  const [fecha, setFecha] = useState("");
  const [pesoBruto, setPesoBruto] = useState("");
  const [pesoNeto, setPesoNeto] = useState("");
  const [precio, setPrecio] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      await api.post("/compras/recepciones", {
        especieId: Number(especieId), bodegaId: Number(bodegaId),
        ordenCompraId: ordenCompraId ? Number(ordenCompraId) : null,
        proveedorId: !ordenCompraId && proveedorId ? Number(proveedorId) : null,
        loteCodigo, fecha, pesoBrutoKg: Number(pesoBruto), pesoNetoKg: Number(pesoNeto), precioCompraKg: Number(precio),
      });
      setModalAbierto(false);
      setEspecieId(""); setBodegaId(""); setOrdenCompraId(""); setProveedorId(""); setLoteCodigo(""); setFecha(""); setPesoBruto(""); setPesoNeto(""); setPrecio("");
      recargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo registrar la recepción.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva recepción</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-borderc">
              <th className="table-th">Lote</th><th className="table-th">Especie</th><th className="table-th">Bodega</th>
              <th className="table-th">Proveedor</th>
              <th className="table-th">Fecha</th><th className="table-th">Peso neto</th><th className="table-th">Precio/kg</th>
              <th className="table-th">Uso</th>
            </tr>
          </thead>
          <tbody>
            {recepciones.map((r) => (
              <tr key={r.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs font-semibold">{r.loteCodigo}</td>
                <td className="table-td">{r.especieNombre}</td>
                <td className="table-td">{r.bodegaNombre}</td>
                <td className="table-td">{r.proveedorNombre || "—"}</td>
                <td className="table-td">{r.fecha}</td>
                <td className="table-td">{r.pesoNetoKg.toLocaleString()} kg</td>
                <td className="table-td">${r.precioCompraKg.toLocaleString()}</td>
                <td className="table-td">
                  <span className={`badge ${r.consumidaEnLote ? "bg-slate-100 text-slate-500" : "bg-accent-light text-accent-dark"}`}>
                    {r.consumidaEnLote ? "en producción" : "disponible"}
                  </span>
                </td>
              </tr>
            ))}
            {recepciones.length === 0 && <tr><td colSpan={8} className="table-td text-center text-textmuted py-8">Sin recepciones registradas.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          <form onSubmit={crear} className="card w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">Nueva recepción de materia prima</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Especie</label>
                <select className="input-field" value={especieId} onChange={(e) => setEspecieId(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {especies.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Bodega destino</label>
                <select className="input-field" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select></div>
            </div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-1">Orden de compra (opcional)</label>
              <select className="input-field" value={ordenCompraId} onChange={(e) => setOrdenCompraId(e.target.value)}>
                <option value="">Sin orden asociada (compra directa)</option>
                {ordenes.map((o) => <option key={o.id} value={o.id}>{o.numero} · {o.proveedorNombre}</option>)}
              </select></div>
            {!ordenCompraId && (
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Proveedor</label>
                <select className="input-field" value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
                <p className="text-[11px] text-textmuted mt-1">Sin orden de compra, el proveedor es obligatorio para poder contabilizar la recepción.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Código de lote</label>
                <input className="input-field font-mono" value={loteCodigo} onChange={(e) => setLoteCodigo(e.target.value)} placeholder="REC-0003" required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
                <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Peso bruto (kg)</label>
                <input className="input-field" type="number" step="0.01" value={pesoBruto} onChange={(e) => setPesoBruto(e.target.value)} required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Peso neto (kg)</label>
                <input className="input-field" type="number" step="0.01" value={pesoNeto} onChange={(e) => setPesoNeto(e.target.value)} required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Precio/kg</label>
                <input className="input-field" type="number" step="0.01" value={precio} onChange={(e) => setPrecio(e.target.value)} required /></div>
            </div>
            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Registrar recepción"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------- Facturas de compra (reventa)
// Camino de entrada de inventario para negocios que no fabrican: una factura
// de un proveedor con varias líneas (producto, cantidad, valor unitario de
// costo) y un valor de venta opcional por línea que ya deja el precio
// sugerido en el mostrador. Cada línea entra a inventario_producto_terminado
// con lote_id=NULL — ver la nota en schema_sqlite.sql y compras_routes.py.
type ItemFormFactura = { producto: string; cantidad: string; valorUnitario: string; valorVenta: string };
const ITEM_VACIO: ItemFormFactura = { producto: "", cantidad: "", valorUnitario: "", valorVenta: "" };

function TabFacturasCompra({ facturas, proveedores, bodegas, catalogoProductos, recargar, puedeCrear }: {
  facturas: FacturaCompra[]; proveedores: Proveedor[]; bodegas: Opcion[]; catalogoProductos: ProductoCatalogo[];
  recargar: () => void; puedeCrear: boolean;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [expandidaId, setExpandidaId] = useState<number | null>(null);
  const [proveedorId, setProveedorId] = useState("");
  const [numeroFactura, setNumeroFactura] = useState("");
  const [fecha, setFecha] = useState("");
  const [bodegaId, setBodegaId] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [items, setItems] = useState<ItemFormFactura[]>([{ ...ITEM_VACIO }]);
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const actualizarItem = (idx: number, campo: keyof ItemFormFactura, valor: string) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));
  };
  const agregarItem = () => setItems((prev) => [...prev, { ...ITEM_VACIO }]);
  const quitarItem = (idx: number) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const totalBase = items.reduce((acc, it) => acc + (Number(it.cantidad) || 0) * (Number(it.valorUnitario) || 0), 0);

  const cerrarModal = () => {
    setModalAbierto(false);
    setProveedorId(""); setNumeroFactura(""); setFecha(""); setBodegaId(""); setObservaciones("");
    setItems([{ ...ITEM_VACIO }]);
    setFormError(null);
  };

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const itemsValidos = items.filter((it) => it.producto.trim() && it.cantidad && it.valorUnitario);
    if (itemsValidos.length === 0) {
      setFormError("Agregue al menos un ítem con producto, cantidad y valor unitario.");
      return;
    }
    setGuardando(true);
    try {
      await api.post("/compras/facturas-compra", {
        proveedorId: Number(proveedorId), numeroFactura, fecha, bodegaId: Number(bodegaId),
        observaciones: observaciones || null,
        items: itemsValidos.map((it) => ({
          producto: it.producto.trim(), cantidad: Number(it.cantidad), valorUnitario: Number(it.valorUnitario),
          valorVenta: it.valorVenta ? Number(it.valorVenta) : null,
        })),
      });
      cerrarModal();
      recargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo registrar la factura de compra.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-3">
        La recepción de mercancía para reventa se hace por medio del ingreso de la factura del proveedor: cada ítem indica
        producto, cantidad y valor unitario de costo, y opcionalmente el valor de venta con el que quedará disponible en
        el mostrador. Cada línea crea inventario propio, aunque el producto se repita entre facturas o proveedores.
      </p>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nueva factura de compra</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-borderc">
              <th className="table-th">Número</th><th className="table-th">Proveedor</th><th className="table-th">Fecha</th>
              <th className="table-th">Bodega</th><th className="table-th">Ítems</th><th className="table-th">Base</th>
              <th className="table-th">Contabilizada</th><th className="table-th"></th>
            </tr>
          </thead>
          <tbody>
            {facturas.map((f) => (
              <React.Fragment key={f.id}>
                <tr className="border-b border-borderc last:border-0">
                  <td className="table-td font-mono text-xs font-semibold">{f.numeroFactura}</td>
                  <td className="table-td">{f.proveedorNombre}</td>
                  <td className="table-td">{f.fecha}</td>
                  <td className="table-td">{f.bodegaNombre}</td>
                  <td className="table-td">{f.items.length}</td>
                  <td className="table-td font-semibold">${f.base.toLocaleString()}</td>
                  <td className="table-td">
                    <span className={`badge ${f.comprobanteId ? "bg-accent-light text-accent-dark" : "bg-slate-100 text-slate-500"}`}>
                      {f.comprobanteId ? "sí" : "no"}
                    </span>
                  </td>
                  <td className="table-td text-right">
                    <button
                      className="text-accent-dark hover:underline text-xs font-semibold"
                      onClick={() => setExpandidaId(expandidaId === f.id ? null : f.id)}
                    >
                      {expandidaId === f.id ? "Ocultar" : "Ver ítems"}
                    </button>
                  </td>
                </tr>
                {expandidaId === f.id && (
                  <tr className="border-b border-borderc last:border-0 bg-slate-50">
                    <td colSpan={8} className="table-td">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left">
                            <th className="text-xs font-semibold text-slate-500 pb-1">Producto</th>
                            <th className="text-xs font-semibold text-slate-500 pb-1">Cantidad</th>
                            <th className="text-xs font-semibold text-slate-500 pb-1">Valor unitario</th>
                            <th className="text-xs font-semibold text-slate-500 pb-1">Valor venta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {f.items.map((it, idx) => (
                            <tr key={idx}>
                              <td className="text-sm py-1">{it.producto}</td>
                              <td className="text-sm py-1">{it.cantidad.toLocaleString()}</td>
                              <td className="text-sm py-1">${it.valorUnitario.toLocaleString()}</td>
                              <td className="text-sm py-1">{it.valorVenta != null ? `$${it.valorVenta.toLocaleString()}` : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {facturas.length === 0 && <tr><td colSpan={8} className="table-td text-center text-textmuted py-8">Sin facturas de compra registradas.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          <form onSubmit={crear} className="card w-full max-w-2xl p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">Nueva factura de compra</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Proveedor</label>
                <select className="input-field" value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Bodega destino</label>
                <select className="input-field" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número de factura</label>
                <input className="input-field font-mono" value={numeroFactura} onChange={(e) => setNumeroFactura(e.target.value)} placeholder="FC-0001" required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
                <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
              <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones</label>
                <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></div>
            </div>

            <div className="border-t border-borderc pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Ítems de la factura</p>
                <button type="button" className="text-accent-dark hover:underline text-xs font-semibold" onClick={agregarItem}>+ Agregar ítem</button>
              </div>
              <datalist id="productos-existentes">
                {catalogoProductos.map((p) => <option key={p.id} value={p.nombre} />)}
              </datalist>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      {idx === 0 && <label className="block text-[11px] font-semibold text-slate-600 mb-1">Producto</label>}
                      <input
                        className="input-field" list="productos-existentes" value={it.producto}
                        onChange={(e) => actualizarItem(idx, "producto", e.target.value)}
                        placeholder="Ej. Camarón congelado importado"
                      />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <label className="block text-[11px] font-semibold text-slate-600 mb-1">Cantidad</label>}
                      <input
                        className="input-field" type="number" step="0.01" min="0" value={it.cantidad}
                        onChange={(e) => actualizarItem(idx, "cantidad", e.target.value)}
                      />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <label className="block text-[11px] font-semibold text-slate-600 mb-1">Vlr. unitario</label>}
                      <input
                        className="input-field" type="number" step="0.01" min="0" value={it.valorUnitario}
                        onChange={(e) => actualizarItem(idx, "valorUnitario", e.target.value)}
                      />
                    </div>
                    <div className="col-span-3">
                      {idx === 0 && <label className="block text-[11px] font-semibold text-slate-600 mb-1">Vlr. venta (opcional)</label>}
                      <input
                        className="input-field" type="number" step="0.01" min="0" value={it.valorVenta}
                        onChange={(e) => actualizarItem(idx, "valorVenta", e.target.value)}
                      />
                    </div>
                    <div className="col-span-1 flex justify-center">
                      <button
                        type="button" className="text-red-600 hover:underline text-xs font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
                        onClick={() => quitarItem(idx)} disabled={items.length === 1}
                      >✕</button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-textmuted mt-2">
                Si el producto no existe aún en el catálogo, se crea automáticamente al guardar la factura (por unidad, sin
                código de barras) — se puede completar después en Productos. El valor de venta, si se indica, queda
                sugerido de inmediato en el mostrador.
              </p>
              <div className="text-right text-sm font-semibold text-slate-700 mt-2">Base total: ${totalBase.toLocaleString()}</div>
            </div>

            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={cerrarModal}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Registrar factura"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------- Documento soporte electrónico
// Ampliación tras comparar con Siigo Nube (pestaña "Soporte por legalizar"): compras
// a proveedores que no van a emitir factura de venta — no depende de una orden de
// compra ni de una recepción con lote/bodega, es un documento contable directo.
function TabDocumentosSoporte({ documentos, proveedores, especies, bodegas, recargar, puedeCrear, puedeEditar }: {
  documentos: DocumentoSoporte[]; proveedores: Proveedor[]; especies: Opcion[]; bodegas: Opcion[]; recargar: () => void; puedeCrear: boolean; puedeEditar: boolean;
}) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [proveedorId, setProveedorId] = useState("");
  const [numero, setNumero] = useState("");
  const [fecha, setFecha] = useState("");
  const [concepto, setConcepto] = useState("");
  const [tipoDestino, setTipoDestino] = useState("gasto");
  const [base, setBase] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [bodegaId, setBodegaId] = useState("");
  const [especieId, setEspecieId] = useState("");
  const [cantidadKg, setCantidadKg] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const crear = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setFormError(null);
    try {
      await api.post("/compras/documentos-soporte", {
        proveedorId: Number(proveedorId), numero, fecha, concepto, tipoDestino, base: Number(base), observaciones: observaciones || null,
        bodegaId: tipoDestino === "inventario" && bodegaId ? Number(bodegaId) : null,
        especieId: tipoDestino === "inventario" && especieId ? Number(especieId) : null,
        cantidadKg: tipoDestino === "inventario" && cantidadKg ? Number(cantidadKg) : null,
      });
      setModalAbierto(false);
      setProveedorId(""); setNumero(""); setFecha(""); setConcepto(""); setTipoDestino("gasto"); setBase(""); setObservaciones("");
      setBodegaId(""); setEspecieId(""); setCantidadKg("");
      recargar();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "No se pudo registrar el documento soporte.");
    } finally {
      setGuardando(false);
    }
  };

  const pagar = async (d: DocumentoSoporte) => {
    if (!window.confirm(`¿Registrar el pago del documento soporte ${d.numero} por $${d.base.toLocaleString()}?`)) return;
    try {
      await api.put(`/compras/documentos-soporte/${d.id}/pagar`, {});
      recargar();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "No se pudo registrar el pago.");
    }
  };

  return (
    <div>
      <p className="text-xs text-textmuted mb-3">
        Para compras a proveedores no obligados a facturar (personas naturales, régimen simple sin factura electrónica): el
        documento soporte reemplaza la factura como respaldo contable y tributario, con su propia numeración independiente.
      </p>
      <div className="flex justify-end mb-3">
        {puedeCrear && <button className="btn-primary" onClick={() => setModalAbierto(true)}>+ Nuevo documento soporte</button>}
      </div>
      <div className="card overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-borderc">
              <th className="table-th">Número</th><th className="table-th">Proveedor</th><th className="table-th">Fecha</th>
              <th className="table-th">Concepto</th><th className="table-th">Destino</th><th className="table-th">Base</th>
              <th className="table-th">Estado</th><th className="table-th"></th>
            </tr>
          </thead>
          <tbody>
            {documentos.map((d) => (
              <tr key={d.id} className="border-b border-borderc last:border-0">
                <td className="table-td font-mono text-xs font-semibold">{d.numero}</td>
                <td className="table-td">{d.proveedorNombre}</td>
                <td className="table-td">{d.fecha}</td>
                <td className="table-td max-w-xs truncate" title={d.concepto}>{d.concepto}</td>
                <td className="table-td"><span className="badge bg-navy/10 text-navy text-[10px]">{d.tipoDestino === "inventario" ? "Inventario MP" : "Gasto"}</span></td>
                <td className="table-td font-semibold">${d.base.toLocaleString()}</td>
                <td className="table-td"><span className={`badge ${d.estado === "pagado" ? "bg-accent-light text-accent-dark" : "bg-amber-50 text-amber-700"}`}>{d.estado}</span></td>
                <td className="table-td text-right">
                  {puedeEditar && d.estado === "pendiente" && (
                    <button className="text-accent-dark hover:underline text-xs font-semibold" onClick={() => pagar(d)}>Registrar pago</button>
                  )}
                </td>
              </tr>
            ))}
            {documentos.length === 0 && <tr><td colSpan={8} className="table-td text-center text-textmuted py-8">Sin documentos soporte registrados.</td></tr>}
          </tbody>
        </table>
      </div>
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
          <form onSubmit={crear} className="card w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="text-base font-semibold text-slate-800">Nuevo documento soporte electrónico</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Proveedor</label>
                <select className="input-field" value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} required>
                  <option value="">Seleccione...</option>
                  {proveedores.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Número</label>
                <input className="input-field font-mono" value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="DS-0001" required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Fecha</label>
                <input className="input-field" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Destino del gasto</label>
                <select className="input-field" value={tipoDestino} onChange={(e) => setTipoDestino(e.target.value)}>
                  <option value="gasto">Gasto</option>
                  <option value="inventario">Inventario de materia prima</option>
                </select></div>
              <div className="col-span-2"><label className="block text-xs font-semibold text-slate-600 mb-1">Concepto</label>
                <input className="input-field" value={concepto} onChange={(e) => setConcepto(e.target.value)} placeholder="Ej. Transporte de materia prima desde finca" required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Base ($)</label>
                <input className="input-field" type="number" step="0.01" value={base} onChange={(e) => setBase(e.target.value)} required /></div>
              <div><label className="block text-xs font-semibold text-slate-600 mb-1">Observaciones</label>
                <input className="input-field" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></div>
            </div>
            {tipoDestino === "inventario" && (
              <div className="grid grid-cols-3 gap-3 rounded-md bg-slate-50 border border-borderc p-3">
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Bodega</label>
                  <select className="input-field" value={bodegaId} onChange={(e) => setBodegaId(e.target.value)} required>
                    <option value="">Seleccione...</option>
                    {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                  </select></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Especie</label>
                  <select className="input-field" value={especieId} onChange={(e) => setEspecieId(e.target.value)} required>
                    <option value="">Seleccione...</option>
                    {especies.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                  </select></div>
                <div><label className="block text-xs font-semibold text-slate-600 mb-1">Cantidad (kg)</label>
                  <input className="input-field" type="number" step="0.01" value={cantidadKg} onChange={(e) => setCantidadKg(e.target.value)} required /></div>
                <p className="text-[11px] text-textmuted col-span-3">
                  Necesario para que la entrada quede registrada también en el kardex físico de materia prima —
                  si no, la cuenta contable de inventario quedaría por encima del saldo físico real.
                </p>
              </div>
            )}
            <p className="text-[11px] text-textmuted">
              El IVA descontable y las retenciones (retefuente, ReteICA) se calculan automáticamente según los atributos
              tributarios del proveedor seleccionado, igual que en una recepción normal.
            </p>
            {formError && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{formError}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setModalAbierto(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Registrar"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
